import _uniq from 'lodash.uniq';
import _sortBy from 'lodash.sortby';

import {
  GL_DRAW_LAYER_PREFIX,
  LABEL_LAYER_PREFIX,
  DEFAULT_HIGHLIGHT_COLOR,
  HIGHLIGHT_SOURCE,
} from '../../constants';
import { isLineString, isPoint, isPolygon } from '../geom';
import { getMapboxLayerStyleOptions } from '../style';

import { vetroLayerIdToMapboxLayerId } from './conversions';
import { getHighlightMapboxLayerId } from './names';
import { toPolygonOutline, isCircleLayer, isPolygonHighlightFillLayer } from './types';

export const getHighlightMapboxLayerProperties = (layer) => {
  let type;

  if (isCircleLayer(layer)) {
    type = 'circle';
  } else if (isPoint(layer)) {
    type = 'symbol';
  } else if (isPolygonHighlightFillLayer(layer)) {
    type = 'fill';
  } else {
    type = 'line';
  }

  const formattedLayer =
    isPolygon(layer) && !isPolygonHighlightFillLayer(layer) ? toPolygonOutline(layer) : layer;

  const styleParams = {
    layer: formattedLayer,
    visibility: 'visible',
    shouldHighlight: true,
    // null is overwritten
    highlightColor: null,
  };

  const styleOptions = getMapboxLayerStyleOptions(styleParams);

  return {
    ...styleOptions,
    filter: ['==', 'layerId', layer.id],
    source: HIGHLIGHT_SOURCE,
    sourceLayer: '',
    type,
  };
};

/**
 * Create a mapbox highlighting layer for a given VETRO layer if the highlight
 * layer does not already exist.
 *
 * NOTE: Creating one mapbox highlight layer per VETRO layer is the best
 * solution we came up with in order for highlight layers to sort properly and
 * have the correct styles when highlight occurs across VETRO layers.
 * @param {*} d destructured args
 * @param {object} d.map mapbox map instance
 * @param {object} d.layer VETRO layer object
 */
export const addHighlightLayer = ({ map, layer }) => {
  const originalLayerMapboxId = vetroLayerIdToMapboxLayerId(layer.id, {
    [layer.id]: layer.geomType,
  });
  const mapboxLayerId = getHighlightMapboxLayerId(
    originalLayerMapboxId,
    isPolygonHighlightFillLayer(layer),
    isCircleLayer(layer),
  );

  if (map.getLayer(mapboxLayerId)) return;

  const { source, ...options } = getHighlightMapboxLayerProperties(layer);

  const { geomType } = layer;
  if (isLineString(geomType)) {
    map.U.addLine(mapboxLayerId, source, options);
  } else if (isPoint(geomType)) {
    map.U.addSymbol(mapboxLayerId, source, options);
  } else {
    // polygon highlight is border of polygon, represented as line
    map.U.addLine(mapboxLayerId, source, options);
  }
};

/**
 * Adds a single GeoJSON data source to the map from which all highlight layers.
 * read. Only adds the source if it does not already exist.
 * @param {object} map mapbox map instance
 */
export const addHighlightSource = (map) => {
  if (!map.getSource(HIGHLIGHT_SOURCE)) {
    map.U.addGeoJSON(HIGHLIGHT_SOURCE);
  }
};

const reverseSortLayerIds = (layerIds, allSortedMapboxLayerIds) => {
  const entries = allSortedMapboxLayerIds.map((layerId, sortIndex) => [layerId, sortIndex]);
  const sortOrderByLayerId = Object.fromEntries(entries);
  return _sortBy(layerIds, (id) => sortOrderByLayerId[id]).reverse();
};

/**
 * Highlight features on the map.
 * @param {*} d destructured args
 * @param {object} d.map mapbox map instance
 * @param {object[]} d.features features for a GeoJSON feature collection.
 * Features in this collection MUST have property layerId.
 * @param {array} d.sortedLayerIds sorted array of all sortable layer ids
 */
export default function highlightFeatures({
  map,
  features,
  sortedMapboxLayerIds: allSortedMapboxLayerIds,
  layerIdGeomTypeMap,
}) {
  addHighlightSource(map);

  const layersIds = _uniq(features.map((f) => f.properties.layerId));
  const mapboxLayerIds = _uniq(
    layersIds.map((layerId) => vetroLayerIdToMapboxLayerId(layerId, layerIdGeomTypeMap)),
  );

  const reverseSortedLayers = reverseSortLayerIds(mapboxLayerIds, allSortedMapboxLayerIds);
  const highlightLayerIds = reverseSortedLayers.map((l) =>
    getHighlightMapboxLayerId(l, isPolygonHighlightFillLayer(l), isCircleLayer(l)),
  );

  // the name of the layer that highlight layers will sit just under
  const regex = new RegExp(`^(${GL_DRAW_LAYER_PREFIX}|${LABEL_LAYER_PREFIX})`);

  const layerToAddBefore = map.getStyle().layers.find((l) => l.id.match(regex))?.id;
  highlightLayerIds.forEach((mapboxLayerId) => map.moveLayer(mapboxLayerId, layerToAddBefore));

  const featureCollection = { type: 'FeatureCollection', features };

  map.U.setData(HIGHLIGHT_SOURCE, featureCollection);
}

/*
 * Returns a feature to be rendered by mapbox in the highlight layers.
 * @param {object} feature original feature; source for geom, props.
 * @param {string|null} [color] The color used for highlighting. Default is yellow.
 * You can explicitly pass color=null to mean "use layer's default styling".
 * Highlighting with a null color can be useful to render a feature with
 * different saturation or z-index.
 */
export const getHighlightingFeature = (
  feature,
  color = DEFAULT_HIGHLIGHT_COLOR,
  opacity = 1,
  fillColor = null,
) => {
  const { geometry } = feature;
  const type = 'Feature';
  const { layerId, vetroId } = feature.xVetro;

  // external features may be MultiPolygon, hence the `includes`
  const properties = {
    layerId,
    color,
    fillColor,
    opacity,
    ...(feature.attributes ?? {}),
    vetro_id: vetroId, // snake case because thats how they appear on regular feature tiles
  };
  return { properties, geometry, type };
};

export const highlightSingleFeature = (map, vetroFeature) => {
  if (vetroFeature) {
    const highlightFeature = getHighlightingFeature(vetroFeature);

    map.U.setData(HIGHLIGHT_SOURCE, highlightFeature);
  } else {
    // The only way I could find to actually reset the data in the source
    // is to pass in an empty feature collection...
    map.U.setData(HIGHLIGHT_SOURCE, { type: 'FeatureCollection', features: [] });
  }
};
